import axios from "axios";

export const STORAGE_KEY = 'AC_API_API';

export const getACApi = () => {
    const data = localStorage.getItem(STORAGE_KEY);

    if (!data) {
        return null;
    };

    return JSON.parse(data);
}

export const getVoiceApi = () => getACApi()?.digital || process.env.REACT_APP_ServerBaseURL;

// Fetch Cognito config from backend API
export const fetchCognitoConfig = async () => {
    try {
        const baseUrl = getVoiceApi();
        const response = await axios.get(`${baseUrl}api/config/v1/`);

        if (response.data?.code === 'success' && response.data?.data) {
            const configItems = response.data.data;
            const config = {};

            // Map config items to cognito config object
            configItems.forEach(item => {
                if (item.configName === 'cognito.domain') {
                    config.domain = item.configValue;
                } else if (item.configName === 'cognito.clientId') {
                    config.clientId = item.configValue;
                } else if (item.configName === 'cognito.responseType') {
                    config.responseType = item.configValue;
                } else if (item.configName === 'cognito.scope') {
                    config.scope = item.configValue;
                }
            });

            // Validate required config values
            const missingFields = [];
            if (!config.domain) missingFields.push('cognito.domain');
            if (!config.clientId) missingFields.push('cognito.clientId');
            if (!config.responseType) missingFields.push('cognito.responseType');
            if (!config.scope) missingFields.push('cognito.scope');

            if (missingFields.length > 0) {
                throw new Error(`Missing required Cognito config: ${missingFields.join(', ')}`);
            }

            // The app authenticates with the ID token, which is the only token carrying the
            // email / given_name / family_name claims.
            //
            // Cognito's /oauth2/authorize only accepts response_type=code or token - there
            // is no "token id_token". With response_type=token it returns BOTH access_token
            // and id_token in the URL fragment, and the openid scope is what produces the
            // ID token. So openid is the thing to validate, not the response type.
            const scopes = config.scope.split(/[\s+]+/).filter(Boolean);

            // The callback reads tokens from the URL fragment, which is the implicit flow.
            // response_type=code would deliver a ?code= that nothing here exchanges, so
            // there is no way to proceed - this one is fatal.
            if (config.responseType.trim().toLowerCase() !== 'token') {
                throw new Error(
                    `Invalid Cognito config: cognito.responseType must be 'token' for the ` +
                    `implicit flow the callback handles (got '${config.responseType}')`
                );
            }

            // The remaining two are warnings, not errors. The backend resolves a user from
            // the Cognito sub, which is present in both the ID and access token, so login
            // still works without these - the user is just keyed on their UUID rather than
            // their email, which is the pre-existing behaviour. Warn loudly so a
            // misconfiguration is visible instead of quietly degrading.
            if (!scopes.includes('openid')) {
                console.warn(
                    `cognito.scope does not include 'openid' (got '${config.scope}'). ` +
                    `Cognito will return no ID token, so users will be keyed on their Cognito ` +
                    `UUID instead of their email address.`
                );
            }
            if (!scopes.includes('profile')) {
                console.warn(
                    `cognito.scope does not include 'profile' (got '${config.scope}'). ` +
                    `Cognito will omit given_name/family_name, so user names will stay as placeholders.`
                );
            }

            return config;
        }

        throw new Error('Failed to fetch Cognito config: Invalid response');
    } catch (error) {
        console.error('Failed to fetch Cognito config:', error);
        throw error;
    }
};
